import {
  CalendarConfigurationDto,
  QueryFactory,
  ResourceType,
} from '@lmt/shared-types';

import {
  CalendarConfigurationFormData,
  defaultCalendarConfigurationData,
} from '@lmt/unicorn-shared/app/components/calendar/configuration/common/types';
import { CalendarFilterSettings } from '@lmt/unicorn-shared/app/components/calendar/configuration/sections/CalendarFilterSettings';
import { CalendarGeneralSettings } from '@lmt/unicorn-shared/app/components/calendar/configuration/sections/CalendarGeneralSettings';
import { CalendarGridSettings } from '@lmt/unicorn-shared/app/components/calendar/configuration/sections/CalendarGridSettings';
import { CalendarPatientsSettings } from '@lmt/unicorn-shared/app/components/calendar/configuration/sections/CalendarPatientsSettings';
import { MiniCalendarSettings } from '@lmt/unicorn-shared/app/components/calendar/configuration/sections/MiniCalendarSettings';
import { CalendarConfigurationContentTree } from '@lmt/unicorn-shared/app/components/calendar/configuration/tree/CalendarConfigurationContentTree';

import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import { SettingsBlock } from '@lmt/unicorn-shared/app/components/common/ui-kit/configuration/SettingsBlock';
import { FormComponentProps } from '@lmt/unicorn-shared/app/components/global-forms/GlobalForms.types';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { UIConfigurationsActions } from '@lmt/unicorn-shared/app/features/ui-configurations/reducer';
import { getUIConfigurations } from '@lmt/unicorn-shared/app/features/ui-configurations/selector';
import { useAdvancedForm } from '@lmt/unicorn-shared/app/helpers/form/advanced-form';
import {
  convertToErrorString,
  handleSubmitFormError,
} from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import TrashIcon from '@lmt/unicorn-shared/app/icons/trash.svg?react';
import {
  useUniDispatch,
  useUniSelector,
} from '@lmt/unicorn-shared/app/store/types';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';

import styles from './CreateCalendarConfiguration.module.scss';
import { useEventCallback } from '@mui/material';
import { useSortedResources } from '@lmt/unicorn-shared/app/api/custom-hooks';

interface Props {
  // id of configuration to edit (if existing configuration is being edited)
  configurationId?: number;
}

export const CreateCalendarConfiguration: React.FC<
  FormComponentProps & Props
> = (props) => {
  const claims = useClaims();
  const dispatch = useUniDispatch();
  const modals = useModal();
  const i18n = useScopedTranslation('calendar_configuration');
  const i18nGlobal = useTranslation();
  const queryClient = useQueryClient();
  const { formType, formId, tryCompleteForm, loadBlocking, configurationId } =
    props;
  const configurationQuery =
    QueryFactory.CalendarConfigurationQuery.useGetCalendarConfigurationQuery(
      configurationId!,
      {
        enabled: !!configurationId,
      },
    );
  const configurationsQuery =
    QueryFactory.CalendarConfigurationQuery.useGetCalendarConfigurationsQuery();
  const existingConfigurations = configurationsQuery.data ?? [];
  const defaultConfigurationId =
    useUniSelector(getUIConfigurations).calendarConfigurationId;

  const { data: calendarSettings } =
    QueryFactory.CalendarSettingsQuery.useGetQuery();

  const localizedName = useMemo(() => {
    if (configurationQuery.data) {
      return i18nGlobal.t(configurationQuery.data.name as any);
    }
  }, [configurationQuery.data]);

  const localizedItem = useMemo(() => {
    if (configurationQuery.data) {
      return {
        ...configurationQuery.data,
        name: localizedName ?? configurationQuery.data.name,
      };
    }
  }, [localizedName, configurationQuery.data]);

  const formMethods = useAdvancedForm<CalendarConfigurationFormData>(
    useEventCallback(async (data: any) => {
      let createdOrUpdatedConfiguration: CalendarConfigurationDto | undefined;
      try {
        await tryCompleteForm(formType, formId, async () => {
          if (configurationId) {
            createdOrUpdatedConfiguration =
              await QueryFactory.CalendarConfigurationClient.editCalendarConfiguration(
                configurationId,
                {
                  ...data,
                  name: configurationQuery.data?.isSystem
                    ? configurationQuery.data.name
                    : data.name,
                },
              );
            await queryClient.invalidateQueries({
              queryKey:
                QueryFactory.CalendarConfigurationQuery.getCalendarConfigurationQueryKey(
                  configurationId,
                ),
            });
            await queryClient.invalidateQueries({
              queryKey: QueryFactory.CalendarSettingsQuery.getQueryKey(),
            });
          } else {
            createdOrUpdatedConfiguration =
              await QueryFactory.CalendarConfigurationClient.createCalendarConfiguration(
                data,
              );
          }
          await queryClient.invalidateQueries({
            queryKey:
              QueryFactory.CalendarConfigurationQuery.getCalendarConfigurationsQueryKey(),
          });
          return createdOrUpdatedConfiguration;
        });
      } catch (e) {
        const errorDetails = handleSubmitFormError(e, setError);
        if (errorDetails.overallServerError) {
          await modals.showError({ text: errorDetails.overallServerError });
        }
      }
    }),
    localizedItem,
    {
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      shouldUnregister: false,
      defaultValues: defaultCalendarConfigurationData,
    },
  );
  const { setError, formState, register } = formMethods;

  const [isShowingTips, setShowingTips] = useState<boolean>(false);
  const [shownTipsCount, setShownTipsCount] = useState<number>(0);

  const isSystem = configurationQuery.data?.isSystem ?? false;

  const places = useSortedResources(ResourceType.Places);

  const specialConditions = useSortedResources(ResourceType.SpecialConditions);

  const doctors = useSortedResources(ResourceType.Doctors);

  const showTip = useEventCallback(() => {
    setShownTipsCount((value) => ++value);
  });

  const hideTip = useEventCallback(() => {
    setShownTipsCount((value) => --value);
  });

  useEffect(() => {
    // turn off tips toggle if user closed all tips manually
    setShowingTips(shownTipsCount > 0);
  }, [shownTipsCount]);

  useEffect(() => {
    if (!isShowingTips) {
      setShownTipsCount(0);
    }
  }, [isShowingTips]);

  const removeConfiguration = useEventCallback(async () => {
    if (!configurationId) {
      return;
    }
    const isConfirmed = await modals.showDeletionConfirmation({
      title: i18n.t('delete.confirm_title'),
      text: i18n.t('delete.confirm_text'),
      okButtonText: i18n.t('delete.confirm_ok_text'),
    });
    if (isConfirmed) {
      loadBlocking(formId, i18n.t('delete.loading'), async () => {
        try {
          await tryCompleteForm(formType, formId, async () => {
            await QueryFactory.CalendarConfigurationClient.removeCalendarConfiguration(
              configurationId,
            );
            if (configurationId === defaultConfigurationId) {
              dispatch(
                UIConfigurationsActions.calendarConfigurationIdChanged(null),
              );
            }
            await queryClient.invalidateQueries({
              queryKey:
                QueryFactory.CalendarConfigurationQuery.getCalendarConfigurationsQueryKey(),
            });
            await queryClient.invalidateQueries({
              queryKey: QueryFactory.CalendarSettingsQuery.getQueryKey(),
            });
          });
        } catch (err) {
          await modals.showError({ text: convertToErrorString(err) });
        }
      }).catch((e) => console.error('removeConfiguration', e));
    }
  });

  const isDuplicateName = useEventCallback((newName: string) => {
    return existingConfigurations.some(
      (x) =>
        x.id !== configurationId &&
        (x.isSystem
          ? i18n.t(`names.${x.name}` as any) === newName
          : x.name === newName),
    );
  });

  return (
    <Loading loading={configurationQuery.isLoading}>
      <form
        onSubmit={formMethods.handleSubmitDefault}
        data-test-id="calendarConfigurationPopup"
      >
        <div className={styles.container}>
          <CalendarConfigurationContentTree />
          <div className={styles.sections}>
            <SettingsBlock
              blockId={'general'}
              blockTitle={i18n.t('blocks.general')}
            >
              <CalendarGeneralSettings
                formMethods={formMethods}
                isSystem={isSystem}
                isDuplicateName={isDuplicateName}
                isShowingTips={isShowingTips}
                setShowingTips={setShowingTips}
              />
            </SettingsBlock>
            <SettingsBlock
              blockId={'calendar_grid'}
              blockTitle={i18n.t('blocks.calendar_grid')}
            >
              <CalendarGridSettings
                formMethods={formMethods}
                isShowingTips={isShowingTips}
                showTip={showTip}
                hideTip={hideTip}
                places={places}
                doctors={doctors}
                specialConditions={specialConditions}
              />
            </SettingsBlock>
            <SettingsBlock
              blockId={'filter_settings'}
              blockTitle={i18n.t('blocks.filter_settings')}
            >
              <CalendarFilterSettings
                places={places}
                specialConditions={specialConditions}
                doctors={doctors}
                formMethods={formMethods}
              />
            </SettingsBlock>
            <SettingsBlock
              blockId={'patients'}
              blockTitle={i18n.t('blocks.patients')}
            >
              <CalendarPatientsSettings formMethods={formMethods} />
            </SettingsBlock>
            <SettingsBlock
              blockId={'mini_calendar'}
              blockTitle={i18n.t('blocks.mini_calendar')}
            >
              <MiniCalendarSettings formMethods={formMethods} />
            </SettingsBlock>
          </div>
        </div>
        <div className={styles.submitContainer}>
          <div>
            {configurationId && (
              <Button
                title={i18n.t('delete.button_name')}
                color={ButtonColor.Danger}
                iconComponent={<TrashIcon />}
                onClick={removeConfiguration}
              />
            )}
          </div>
          <div className={styles.bottomControls}>
            {claims.permissions.manageSharedCalendarConfigurations && (
              <CheckBox
                {...register('isShared')}
                title={i18n.t('is_shared')}
                disabled={
                  isSystem ||
                  configurationId === calendarSettings?.defaultCalendarView
                }
              />
            )}
            <SubmitButton
              submitting={formState.isSubmitting}
              className={styles.submitButton}
              title={i18n.t('submit.save')}
            />
          </div>
        </div>
      </form>
    </Loading>
  );
};
